import React, { useState } from 'react';
import { auth, database, storage } from '../../firebaseConfig';
import { ref as dbRef, update, remove } from 'firebase/database';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import './CategoryManagementModal.css';

function CategoryManagementModal({ category, onClose, onCategoryUpdated }) {
    const [formData, setFormData] = useState({
        name: category.name || '',
        description: category.description || '',
        tier: category.tier || 'team'
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(category.imageUrl || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('Image file size must be less than 5MB');
                return;
            }

            setImageFile(file);
            setError('');

            // Create preview
            const reader = new FileReader();
            reader.onload = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadImage = async (file) => {
        if (!auth.currentUser || !file) return null;

        const fileName = `academy/categories/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, fileName);
        
        try {
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            return downloadURL;
        } catch (error) {
            console.error('Error uploading image:', error);
            throw new Error('Failed to upload image');
        }
    };

    const deleteOldImage = async (imageUrl) => {
        if (!imageUrl || !imageUrl.includes('firebase')) return;
        
        try {
            // Extract path from Firebase Storage URL
            const url = new URL(imageUrl);
            const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
            if (pathMatch) {
                const imagePath = decodeURIComponent(pathMatch[1]);
                const imageRef = ref(storage, imagePath);
                await deleteObject(imageRef);
            }
        } catch (error) {
            console.warn('Could not delete old image:', error);
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            setError('Category name is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            let imageUrl = category.imageUrl;
            
            // Upload new image if provided
            if (imageFile) {
                // Delete old image first
                if (category.imageUrl) {
                    await deleteOldImage(category.imageUrl);
                }
                imageUrl = await uploadImage(imageFile);
            }

            // Update category data
            const updates = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                tier: formData.tier,
                imageUrl: imageUrl,
                updatedAt: new Date().toISOString()
            };

            await update(dbRef(database, `Academy/categories/${category.id}`), updates);
            
            onCategoryUpdated();
            onClose();

        } catch (error) {
            console.error('Error updating category:', error);
            setError('Failed to update category. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Are you sure you want to delete the category "${category.name}"? This will also delete all videos in this category.`)) {
            return;
        }

        setLoading(true);
        try {
            // Delete category image if exists
            if (category.imageUrl) {
                await deleteOldImage(category.imageUrl);
            }

            // Delete the category
            await remove(dbRef(database, `Academy/categories/${category.id}`));
            
            // Note: Videos will be orphaned - you might want to handle this differently
            // For now, they'll still exist but won't show up in the UI
            
            onCategoryUpdated();
            onClose();

        } catch (error) {
            console.error('Error deleting category:', error);
            setError('Failed to delete category. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    return (
        <div className="academy-category-management-overlay">
            <div className="academy-category-management-modal">
                <div className="academy-modal-header">
                    <h3>Edit Category</h3>
                    <button className="academy-close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="academy-modal-content">
                    {error && <div className="academy-error-message">{error}</div>}

                    <div className="academy-form-group">
                        <label htmlFor="name">Category Name *</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="e.g., Customer Acquisition, Sales Techniques..."
                            maxLength={50}
                            required
                        />
                        <small className="academy-char-count">{formData.name.length}/50</small>
                    </div>

                    <div className="academy-form-group">
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Describe what this category covers..."
                            rows={3}
                            maxLength={200}
                        />
                        <small className="academy-char-count">{formData.description.length}/200</small>
                    </div>

                    <div className="academy-form-group">
                        <label>Access Tier</label>
                        <div className="academy-tier-selector">
                            {['team', 'standard', 'premium'].map(tier => (
                                <button
                                    key={tier}
                                    type="button"
                                    className={`academy-tier-btn academy-tier-${tier} ${formData.tier === tier ? 'active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, tier }))}
                                >
                                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="academy-form-group">
                        <label htmlFor="image">Category Image</label>
                        <div className="academy-image-upload-area">
                            {imagePreview ? (
                                <div className="academy-image-preview">
                                    <img src={imagePreview} alt="Category preview" />
                                    <button 
                                        type="button" 
                                        className="academy-remove-image-btn"
                                        onClick={removeImage}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                <div className="academy-image-upload-placeholder">
                                    <div className="academy-upload-icon">📁</div>
                                    <p>Choose an image for this category</p>
                                    <small>JPG, PNG up to 5MB</small>
                                </div>
                            )}
                            <input
                                type="file"
                                id="image"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="academy-image-input"
                            />
                        </div>
                    </div>
                </div>

                <div className="academy-modal-actions">
                    <button 
                        className="academy-delete-btn"
                        onClick={handleDelete}
                        disabled={loading}
                    >
                        🗑️ Delete Category
                    </button>
                    
                    <div className="academy-action-group">
                        <button 
                            className="academy-cancel-btn"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            className="academy-save-btn"
                            onClick={handleSave}
                            disabled={loading || !formData.name.trim()}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CategoryManagementModal;
